import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroup } from '../context/GroupContext';
import groupService from '../api/groupService';
import '../styles/GroupCreationSuccess.css';

const GroupCreationSuccess = () => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('Creating your community...');
  const [isComplete, setIsComplete] = useState(false);
  const { groupId } = useParams();
  const { refreshAdminGroup } = useGroup();
  const navigate = useNavigate();

  const steps = [
    'Creating your community...',
    'Setting up admin permissions...',
    'Preparing community features...',
    'Finalizing setup...',
    'Success! Welcome to your community!'
  ];

  useEffect(() => {
    const runCreationProcess = async () => {
      try {
        // Step 1: Initial creation (0-20%)
        setCurrentStep(steps[0]);
        await animateProgress(0, 20, 1000);

        // Step 2: Admin permissions (20-40%)
        setCurrentStep(steps[1]);
        await animateProgress(20, 40, 800);

        // Step 3: Community features (40-70%)
        setCurrentStep(steps[2]);
        await animateProgress(40, 70, 1000);
        
        // Step 4: Finalizing (70-90%)
        setCurrentStep(steps[3]);
        await animateProgress(70, 90, 800);
        
        // Simple context refresh - the group should already be in context from CreateGroup
        console.log('� Refreshing admin groups context...');
        await refreshAdminGroup();

        // Step 5: Success! (90-100%)
        setCurrentStep(steps[4]);
        await animateProgress(90, 100, 500);
        
        // Mark as complete and show success animation
        setIsComplete(true);
        
        // Wait for user to enjoy the success message
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Navigate to the group with welcome parameter
        console.log('✅ Navigating to group page...');
        navigate(`/group/${groupId}?welcome=true`);
        
      } catch (error) {
        console.error('Error in creation process:', error);
        setCurrentStep('Something went wrong. Redirecting...');
        setTimeout(() => {
          navigate(`/group/${groupId}`);
        }, 2000);
      }
    };

    if (groupId) {
      runCreationProcess();
    } else {
      // No group ID, redirect to dashboard
      navigate('/dashboard');
    }
  }, [groupId, navigate, refreshAdminGroup]);

  const animateProgress = (start, end, duration) => {
    return new Promise(resolve => {
      const startTime = Date.now();
      const difference = end - start;
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progressRatio = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progressRatio, 3); // Ease-out animation
        
        const currentProgress = start + (difference * easeOut);
        setProgress(currentProgress);
        
        if (progressRatio < 1) {
          requestAnimationFrame(updateProgress);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(updateProgress);
    });
  };

  return (
    <div className="group-creation-success">
      <div className="success-container">
        {/* Logo or Icon */}
        <div className="success-icon">
          {isComplete ? (
            <div className="checkmark-animation">
              <svg className="checkmark" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="m14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          ) : (
            <div className="loading-icon">
              <div className="spinner"></div>
            </div>
          )}
        </div>

        {/* Progress Text */}
        <h1 className={`success-title ${isComplete ? 'complete' : ''}`}>
          {isComplete ? 'Community Created Successfully!' : 'Creating Your Community'}
        </h1>
        
        <p className="success-subtitle">
          {currentStep}
        </p>

        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>

        {/* Success Message */}
        {isComplete && (
          <div className="success-message">
            <p>Redirecting you to your new community...</p>
          </div>
        )}

        {/* Animated Background Elements */}
        <div className="background-animation">
          <div className="floating-element element-1"></div>
          <div className="floating-element element-2"></div>
          <div className="floating-element element-3"></div>
          <div className="floating-element element-4"></div>
        </div>
      </div>
    </div>
  );
};

export default GroupCreationSuccess;